import React from "react";
import {
  useForm,
  FormProvider,
  SubmitHandler,
  ValidationMode,
  UseFormReturn,
  FieldValues,
  FieldErrors,
  DefaultValues,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { emptySchema } from "./validationSchema";

import(/* webpackChunkName: "form-styles" */ "./styles.css");

export default function Form<FormFields extends FieldValues>({
  children,
  validationSchema,
  onSubmit,
  resetOnSubmit = false,
  action,
  method,
  formId,
  validationMode = "onBlur",
  reValidateMode = "onBlur",
  formMethodsRef,
  shouldFocusError = false,
  onValidationFailed,
  tabIndexNumber,
  ariaLabelBy = "",
  formRef,
  defaultValues,
}: {
  children: React.ReactElement | React.ReactElement[];
  validationSchema?: z.Schema;
  onSubmit?: SubmitHandler<FormFields>;
  resetOnSubmit?: boolean;
  action?: string;
  method?: string;
  formId?: string;
  validationMode?: keyof ValidationMode;
  reValidateMode?: Exclude<keyof ValidationMode, "onTouched" | "all">;
  formMethodsRef?: { current: UseFormReturn<FormFields> | undefined };
  shouldFocusError?: boolean;
  onValidationFailed?: (errors: FieldErrors<FieldValues>) => void;
  tabIndexNumber?: number;
  ariaLabelBy?: string;
  formRef?: React.LegacyRef<HTMLFormElement>;
  defaultValues?: DefaultValues<FormFields>;
}) {
  const methods = useForm<FormFields>({
    mode: validationMode,
    shouldFocusError,
    reValidateMode,
    resolver: zodResolver(validationSchema || emptySchema),
    defaultValues,
  });

  if (formMethodsRef) {
    formMethodsRef.current = methods;
  }

  const onSubmitWrapper: SubmitHandler<FormFields> = (data, event) => {
    if (onSubmit) {
      onSubmit(data, event);
    }

    if (resetOnSubmit) {
      methods.reset();
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        action={action}
        method={method}
        onSubmit={methods.handleSubmit(onSubmitWrapper, onValidationFailed)}
        ref={formRef}
        tabIndex={tabIndexNumber}
        {...(ariaLabelBy.length > 0 && {
          "aria-labelledby": `${ariaLabelBy}`,
        })}
      >
        {children}
      </form>
    </FormProvider>
  );
}
